import { useCallback, useMemo, useState } from "react";
import FileProcessingStatusModal from "../../components/FileProcessingStatusModal";
import DropzoneOverlay from "../../components/DropzoneOverlay";
import { FiChevronDown } from "react-icons/fi";
import { Run } from "@quantizr-engineering/common/types/run";
import { useRunService } from "../../api/runService";
import { useShowService } from "../../api/showService";
import { useNavigate } from "react-router-dom";
import CreateRunsModal from "../../components/CreateRunsModal";
import {
  FileEntity,
  FileStatus,
} from "@quantizr-engineering/common/types/file";
import {
  ContractType,
  showsForRun,
  ShowStatus,
} from "@quantizr-engineering/common/types/show";
import AddRunsDropdown from "./components/AddRunDropdown";
import { FaCalendarAlt } from "react-icons/fa";
import {
  Chart as ChartJS,
  ChartOptions,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import {
  calculateRunFigures,
  calculateShowFigures,
} from "@quantizr-engineering/common/math/live";
import FileModal from "../../components/FileModal";
import {
  formatCurrency,
  formatPercentWithSignAndPercentage,
} from "../../utils/numberFormatters";
import ReusableFormikWrapper from "../../contexts/ReusableFormikWrapper";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
);

export type ProcessingFile =
  | { id: number; filename: string; status: FileStatus | string | undefined }
  | File;

const Runs = () => {
  const [showFileProcessingStatusModal, setShowFileProcessingStatusModal] =
    useState(false);
  const [showCreateRunModal, setShowCreateRunModal] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const [showFileModal, setShowFileModal] = useState(false);
  const [processingFiles, setProcessingFiles] = useState<Partial<FileEntity>[]>(
    []
  );

  const { runs, getRuns } = useRunService();
  const { shows, getShows } = useShowService();
  const navigate = useNavigate();

  const sortedRuns = useMemo(() => {
    if (!runs?.length) return [];
    return runs.sort((a, b) => {
      const aShows = showsForRun(shows, a);
      const bShows = showsForRun(shows, b);
      if (!aShows.length) return 1;
      if (!bShows.length) return -1;
      const earliestAShow = Math.min(
        ...aShows.map((show) => show.performanceDate.getTime())
      );
      const earliestBShow = Math.min(
        ...bShows.map((show) => show.performanceDate.getTime())
      );

      return earliestAShow - earliestBShow;
    });
  }, [runs, shows]);

  const totalRuns = runs?.length || 0;
  const totalShows = shows?.length
    ? shows.filter((show) => show.runID).length
    : 0;
  const totalRevenue =
    runs?.reduce(
      (acc, run) =>
        acc + calculateRunFigures(run, showsForRun(shows, run)).walkoutActual,
      0
    ) || 0;
  const totalExpenses =
    runs?.reduce(
      (acc, run) =>
        acc +
        calculateRunFigures(run, showsForRun(shows, run))
          .artistExpensesProjected,
      0
    ) || 0;
  const profitMargin =
    totalRevenue > 0
      ? ((totalRevenue - totalExpenses) / totalRevenue) * 100
      : 0;

  const getDateRanges = () => {
    const today = new Date();

    // This month - first day to last day of current month
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      0
    );

    // Past year - first day of current year to today
    const firstDayOfYear = new Date(today.getFullYear(), 0, 1);

    return {
      thisMonth: {
        start: firstDayOfMonth,
        end: lastDayOfMonth,
      },
      yearToDate: {
        start: firstDayOfYear,
        end: today,
      },
    };
  };

  const dateRanges = getDateRanges();

  const dateRangeOptions = [
    {
      label: "This month",
      value: "thisMonth",
      range: dateRanges.thisMonth,
    },
    { label: "Past 7 days", value: "7days" },
    { label: "Past 30 days", value: "30days" },
    { label: "Past 3 months", value: "3months" },
    { label: "Past 6 months", value: "6months" },
    {
      label: "Year to date",
      value: "yearToDate",
      range: dateRanges.yearToDate,
    },
    { label: "All Time", value: "allTime" },
  ];
  const [selectedValue, setSelectedValue] = useState(dateRangeOptions[6].value);

  const chartOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom" as const,
        align: "start",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 8,
          boxHeight: 8,
        },
      },
      datalabels: {
        display: false,
      },
    },
    scales: {
      x: {
        border: {
          display: false,
        },
        grid: {
          display: false,
        },
        beginAtZero: true,
        ticks: {
          callback: function (_value: any, index: number, _ticks: any) {
            const str = this.getLabelForValue(index);
            return str.length > 13 ? str.substring(0, 10) + "..." : str;
          },
        },
      },
      y: {
        type: "linear" as const,
        position: "right",
        beginAtZero: true,
        border: {
          display: false,
        },
        grid: {
          display: false,
        },
        ticks: {
          callback: function (value: number | string) {
            if (typeof value === "number") {
              return formatCurrency(value);
            }
            return value;
          },
        },
      },
    },
  };

  const chartData = {
    labels: sortedRuns
      ?.filter((run) => showsForRun(shows, run).length > 0)
      .map((run) => run.name),
    datasets: [
      {
        label: "Artist Take-Home",
        data:
          sortedRuns?.map(
            (run) =>
              calculateRunFigures(run, showsForRun(shows, run)).walkoutActual ||
              0
          ) || [],
        borderColor: "#35C1ED",
        backgroundColor: (context: any) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return null;
          const gradient = ctx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom
          );
          gradient.addColorStop(0, "rgba(53, 193, 237, 0.4)");
          gradient.addColorStop(1, "rgba(53, 193, 237, 0)");
          return gradient;
        },
        tension: 0,
        pointRadius: 2,
        fill: true,
        borderWidth: 2,
      },
      {
        label: "Expenses",
        data: sortedRuns?.map(
          (run) =>
            calculateRunFigures(run, showsForRun(shows, run))
              .artistExpensesProjected
        ),
        borderColor: "#A994FF",
        backgroundColor: (context: any) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return null;
          const gradient = ctx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom
          );
          gradient.addColorStop(0, "rgba(169, 148, 255, 0.8)");
          gradient.addColorStop(1, "rgba(169, 148, 255, 0)");
          return gradient;
        },
        tension: 0,
        pointRadius: 2,
        fill: true,
        borderWidth: 2,
      },
    ],
  };

  const getRunDates = useCallback(
    (run: Run) => {
      const runShows = showsForRun(shows, run);
      if (!runShows || runShows.length < 1) return "N/A";

      if (runShows.length === 1) {
        return runShows[0].performanceDate.toLocaleDateString("en-US", {
          month: "numeric",
          day: "numeric",
          year: "2-digit",
        });
      }

      return `${new Date(
        Math.min(...runShows.map((show) => show.performanceDate.getTime()))
      ).toLocaleDateString("en-US", {
        month: "numeric",
        day: "numeric",
        year: "2-digit",
      })} - ${new Date(
        Math.max(...runShows.map((show) => show.performanceDate.getTime()))
      ).toLocaleDateString("en-US", {
        month: "numeric",
        day: "numeric",
        year: "2-digit",
      })}`;
    },
    [shows]
  );

  const handleDrop = (files: File[]) => {
    setDroppedFiles(files);
    setShowFileModal(true);
  };

  const getStatusCount = useCallback(
    (run: Run, status: ShowStatus) => {
      const showsWithStatus = shows?.filter(
        (show) => show.runID === run.id && show.status === status
      );
      return showsWithStatus?.length || 0;
    },
    [shows]
  );

  if (!runs || !shows) return <div>Loading...</div>;

  return (
    <div className="flex flex-row space-x-4 justify-center bg-white h-screen">
      <div className="w-full px-4">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Live</h1>
          <div className="px-4 pt-2">
            <AddRunsDropdown
              onCreateRun={() => setShowCreateRunModal(true)}
              onAddFiles={() => setShowFileModal(true)}
            />
          </div>
        </div>
        {!showFileModal && <DropzoneOverlay onDrop={handleDrop} />}
        {showFileModal && (
          <FileModal
            initialShowId={-1}
            initialRunId={-1}
            initialFiles={droppedFiles}
            onClose={(fileEntities: Partial<FileEntity>[]) => {
              setProcessingFiles(fileEntities);
              setShowFileModal(false);
              if (fileEntities?.length > 0) {
                setShowFileProcessingStatusModal(true);
              }
            }}
            defaultType={ContractType.LIVE_MUSIC_AGREEMENT}
          />
        )}

        {showFileProcessingStatusModal && (
          <FileProcessingStatusModal
            files={processingFiles}
            onClose={() => {
              getShows();
              getRuns();
              setShowFileProcessingStatusModal(false);
              window.location.reload();
            }}
          />
        )}
        <CreateRunsModal
          isOpen={showCreateRunModal}
          onClose={() => {
            setShowCreateRunModal(false);
            window.location.reload();
          }}
        />
        <ReusableFormikWrapper data={runs}>
          {({ values }) => {
            const totalRuns = runs?.length || 0;
            const totalShows = shows?.length
              ? shows.filter((show) => show.runID).length
              : 0;

            const sortedRuns = values.sort((a, b) => {
              const aShows = showsForRun(shows, a);
              const bShows = showsForRun(shows, b);
              if (!aShows.length) return 1;
              if (!bShows.length) return -1;
              const earliestAShow = Math.min(
                ...aShows.map((show) => show.performanceDate.getTime())
              );
              const earliestBShow = Math.min(
                ...bShows.map((show) => show.performanceDate.getTime())
              );

              return earliestAShow - earliestBShow;
            });

            return (
              <>
                <div className="flex items-center bg-white rounded-lg">
                  <div className="flex items-center">
                    <FaCalendarAlt className="text-[#48455B] mr-2" />
                    <span className="text-[#48455B] font-medium text-sm mr-1">
                      Financial snapshot
                    </span>
                    <span className="mx-2">-</span>
                  </div>
                  <div className="dropdown ml-1">
                    <label
                      tabIndex={0}
                      className="flex items-center space-x-2 cursor-pointer"
                    >
                      <span className="text-black text-sm font-semibold">
                        {dateRangeOptions.find(
                          (opt) => opt.value === selectedValue
                        )?.label || "Select period"}
                      </span>
                      <FiChevronDown />
                    </label>

                    <ul
                      tabIndex={0}
                      className="dropdown-content menu p-2 shadow bg-white rounded-lg border border-[#D9D8E1] w-60"
                    >
                      {dateRangeOptions.map((option) => (
                        <li key={option.value}>
                          <a
                            className="flex items-center space-x-3"
                            onClick={() => setSelectedValue(option.value)}
                          >
                            <div
                              className={`w-5 h-5 rounded-full ${
                                option.value === selectedValue
                                  ? "border-[#6D53FD] border-2"
                                  : "border-[#BAB8C9] border"
                              } flex items-center justify-center`}
                            >
                              {option.value === selectedValue && (
                                <div className="w-3 h-3 rounded-full bg-[#6D53FD]" />
                              )}
                            </div>

                            <span className="text-[#48455B] text-sm">
                              {option.label}
                            </span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="flex gap-2" style={{ height: "272px" }}>
                  <div className="w-1/2 flex rounded-lg border border-[#D9D8E1] flex-col h-full gap-2">
                    <div className="h-1/3 rounded-lg p-2">
                      <div className="grid grid-cols-2 h-full">
                        <div className="flex flex-col">
                          <div>
                            <span className="text-[#48455B] text-sm">
                              Artist take-home
                            </span>
                          </div>
                          <div>
                            <span className="text-black text-3xl font-bold">
                              {formatCurrency(
                                runs?.reduce(
                                  (total, run) =>
                                    total +
                                    calculateRunFigures(
                                      run,
                                      showsForRun(shows, run)
                                    ).artistTakeHomeActual,
                                  0
                                ) || 0
                              )}
                            </span>
                          </div>
                          <div className="hidden">
                            <span className="text-[#48455B] text-sm">
                              increase/decrease
                            </span>
                          </div>
                        </div>

                        <div className="p-2 h-full">
                          <div className="grid grid-cols-2 gap-2 h-full">
                            <div className="flex flex-col justify-between">
                              <div>
                                <span className="text-[#48455B] text-sm">
                                  Revenue
                                </span>
                              </div>
                              <div>
                                <span className="text-[#48455B] text-sm">
                                  Expenses
                                </span>
                              </div>
                              <div>
                                <span className="text-[#48455B] text-sm">
                                  Profit Margin
                                </span>
                              </div>
                            </div>

                            <div className="flex flex-col justify-between text-right">
                              <div>
                                <span className="text-black text-sm font-bold">
                                  {formatCurrency(totalRevenue)}
                                </span>
                              </div>
                              <div>
                                <span className="text-black text-sm font-bold">
                                  {formatCurrency(totalExpenses)}
                                </span>
                              </div>
                              <div>
                                <span className="text-black text-sm font-bold">
                                  {formatPercentWithSignAndPercentage(
                                    profitMargin
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="h-2/3 p-2 rounded-lg overflow-hidden">
                      <Line options={chartOptions} data={chartData} />
                    </div>
                  </div>

                  <div className="w-1/2 flex flex-col h-full gap-2">
                    <div className="h-1/3 border border-[#D9D8E1] rounded-lg">
                      <div className="grid grid-cols-2">
                        <div className="p-4">
                          <div className="text-[#48455B] text-sm">
                            Total runs
                          </div>
                          <div className="text-3xl font-bold text-black">
                            {totalRuns}
                          </div>
                        </div>
                        <div className="p-4">
                          <div className="text-[#48455B] text-sm">
                            Total shows
                          </div>
                          <div className="text-3xl font-bold text-black">
                            {totalShows}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="h-2/3 rounded-lg overflow-hidden">
                      <div className="grid grid-cols-2 grid-rows-2 h-full gap-2">
                        <div className="bg-[#E9E8ED] rounded-lg p-4">
                          <div className="bg-[#7D7899] px-2 rounded-lg inline-flex items-center">
                            <span className="text-white text-sm font-semibold whitespace-nowrap">
                              Draft
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="text-3xl font-bold text-[#48455B]">
                              {shows?.filter(
                                (show) => show.status === ShowStatus.DRAFT
                              ).length || 0}
                            </div>
                            <div className="text-sm text-[#48455B]">
                              {formatCurrency(
                                shows
                                  ?.filter(
                                    (show) => show.status === ShowStatus.DRAFT
                                  )
                                  .reduce(
                                    (total, show) =>
                                      total +
                                      calculateShowFigures(show).walkout,
                                    0
                                  ) || 0
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="bg-[#F1E5FF] rounded-lg p-4">
                          <div className="bg-[#4932F5] px-2 rounded-lg inline-flex items-center">
                            <span className="text-white text-sm font-semibold whitespace-nowrap">
                              Confirmed
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="text-3xl font-bold text-[#1A25BC]">
                              {shows?.filter(
                                (show) => show.status === ShowStatus.CONFIRMED
                              ).length || 0}
                            </div>
                            <div className="text-sm text-[#48455B]">
                              {formatCurrency(
                                shows
                                  ?.filter(
                                    (show) =>
                                      show.status === ShowStatus.CONFIRMED
                                  )
                                  .reduce(
                                    (total, show) =>
                                      total + (show.guarantee || 0),
                                    0
                                  ) || 0
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="bg-[#E2F4FC] rounded-lg p-4">
                          <div className="bg-[#19AEDE] px-2 rounded-lg inline-flex items-center">
                            <span className="text-white text-sm font-semibold whitespace-nowrap">
                              Settled
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="text-3xl font-bold text-[#0D5F7F]">
                              {shows?.filter(
                                (show) => show.status === ShowStatus.SETTLED
                              ).length || 0}
                            </div>
                            <div className="text-sm text-[#48455B]">
                              {formatCurrency(
                                shows
                                  ?.filter(
                                    (show) => show.status === ShowStatus.SETTLED
                                  )
                                  .reduce(
                                    (total, show) =>
                                      total + (show.guarantee || 0),
                                    0
                                  ) || 0
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="bg-[#D1FAE2] rounded-lg p-4">
                          <div className="bg-[#038456] px-2 rounded-lg inline-flex items-center">
                            <span className="text-white text-sm font-semibold whitespace-nowrap">
                              Paid
                            </span>
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="text-3xl font-bold text-[#054F36]">
                              {shows?.filter(
                                (show) => show.status === ShowStatus.PAID
                              ).length || 0}
                            </div>
                            <div className="text-sm text-[#48455B]">
                              {formatCurrency(
                                shows
                                  ?.filter(
                                    (show) => show.status === ShowStatus.PAID
                                  )
                                  .reduce(
                                    (total, show) =>
                                      total + (show.guarantee || 0),
                                    0
                                  ) || 0
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col overflow-y-auto overflow-x-hidden h-[524px] pb-32">
                  <table className="min-w-full">
                    <thead
                      className="sticky top-0 z-10 bg-white 
  after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[1px] after:bg-[#D9D8E1]
  before:absolute before:top-0 before:left-0 before:right-0 before:h-[1px] before:bg-[#D9D8E1]"
                    >
                      <tr>
                        <th className="w-[25%] px-6 py-1 text-left text-sm font-semibold text-black tracking-wider">
                          Name
                        </th>
                        <th className="w-[25%] px-6 py-1 text-left text-sm font-semibold text-black tracking-wider">
                          Status
                        </th>
                        <th className="w-[25%] px-6 py-1 text-left text-sm font-semibold text-black tracking-wider">
                          Walkout
                        </th>
                        <th className="w-[25%] px-6 py-1 text-left text-sm font-semibold text-black tracking-wider">
                          Artist Take-Home
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {sortedRuns && sortedRuns.length > 0 ? (
                        sortedRuns.map((run) => (
                          <tr
                            key={run.id}
                            className="hover:bg-gray-50 cursor-pointer transition-all duration-150"
                            onClick={() => navigate(`/runs/${run.id}`)}
                            onAuxClick={(e) => {
                              e.preventDefault();
                              window.open(
                                `/runs/${run.id}`,
                                "_blank",
                                "noopener,noreferrer"
                              );
                            }}
                          >
                            <td className="px-6 py-2 text-sm text-gray-700">
                              <div className="flex flex-col items-start">
                                <span className="font-bold text-black text-xl">
                                  {run.name}
                                </span>
                                <span className="text-[#48455B] text-sm">
                                  {getRunDates(run)}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              <div className="flex justify-between w-[90%]">
                                <span className="text-[#48455B] text-sm font-semibold rounded">
                                  {getStatusCount(run, ShowStatus.DRAFT)} draft
                                </span>
                                <span className="text-[#4932F5] text-sm font-semibold rounded">
                                  {getStatusCount(run, ShowStatus.CONFIRMED)}{" "}
                                  confirmed
                                </span>
                                <span className="text-[#19AEDE] text-sm font-semibold rounded">
                                  {getStatusCount(run, ShowStatus.SETTLED)}{" "}
                                  settled
                                </span>
                                <span className="text-[#038456] text-sm font-semibold rounded">
                                  {getStatusCount(run, ShowStatus.PAID)} paid
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-[#48455B]">
                              {formatCurrency(
                                calculateRunFigures(
                                  run,
                                  showsForRun(shows, run)
                                ).walkoutActual
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm text-[#48455B]">
                              {formatCurrency(
                                calculateRunFigures(
                                  run,
                                  showsForRun(shows, run)
                                ).artistTakeHomeActual
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={4}
                            className="px-6 py-4 text-center text-gray-500"
                          >
                            No Runs Available.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            );
          }}
        </ReusableFormikWrapper>
      </div>
    </div>
  );
};

export default Runs;
